import { Box, Divider, useMediaQuery, useTheme } from "@mui/material";
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// import { StoreIntelligenceSection } from '@/components/layout/Sidebar/StoreIntelligenceSection';
import { CraveAdminSection } from "@/components/layout/Sidebar/CraveAdminSection";
import { hasAvailableReports, ReportsSection } from "@/components/layout/Sidebar/ReportsSection";
import { StoreViewSection } from "@/components/layout/Sidebar/StoreViewSection";
import { UtilitiesSection } from "@/components/layout/Sidebar/UtilitiesSection";
import { useAuth } from "@/contexts/AuthContext";
import { paths } from "@/routes/paths";
import { usePermission } from "@/utils/permissionManager";
import { UserRole } from "@/utils/roleManager";

interface SidebarProps {
  isMobile?: boolean;
  // Called after any item is selected (used to close mobile drawer)
  onItemSelect?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isMobile, onItemSelect }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { user, logout } = useAuth();
  const { userHasPermission } = usePermission(user?.role || "");

  // Initialize which sections should be expanded based on current location
  const initialExpandedSections = useMemo(() => {
    const path = location.pathname;
    const utilityRoutes = [
      "/utilities",
      paths.utilities.userManagement,
      paths.utilities.faq,
      paths.utilities.feedback,
    ];
    if (utilityRoutes.some((utilityRoute) => path.startsWith(utilityRoute))) {
      return { storeView: false, reports: false, craveAdmin: false, utilities: true };
    }
    if (path.startsWith("/admin") || path.startsWith(paths.utilities.profile)) {
      return { storeView: false, reports: false, craveAdmin: true, utilities: false };
    }
    if (path.startsWith("/reports")) {
      return { storeView: false, reports: true, craveAdmin: false, utilities: false };
    }
    if (path.startsWith(paths.storeInsights.root)) {
      return { storeView: true, reports: false, craveAdmin: false, utilities: false };
    }

    return { storeView: true, reports: false, craveAdmin: false, utilities: false };
  }, [location.pathname]);

  const [expandedSections, setExpandedSections] = useState(initialExpandedSections);

  // Keep expanded sections in sync with location when it changes
  React.useEffect(() => {
    setExpandedSections(initialExpandedSections);
  }, [initialExpandedSections]);

  // toggleSection moved below after visibility is computed

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login", { replace: true });
    } catch {
      // noop
    }
  };

  if (!isDesktop && !isMobile) {
    return null;
  }

  const canSeeStoreView = [UserRole.ASSOCIATE, UserRole.STORE_ADMIN].some((role) =>
    userHasPermission(role),
  );
  const canSeeCraveAdmin = [UserRole.CRAVE_EMPLOYEE, UserRole.CRAVE_ADMIN].some((role) =>
    userHasPermission(role),
  );
  const canSeeReports = hasAvailableReports(userHasPermission);

  const getVisibleSections = () => {
    const sections: Array<"storeView" | "reports" | "craveAdmin" | "utilities"> = [];
    if (canSeeStoreView) sections.push("storeView");
    if (canSeeReports) sections.push("reports");
    if (canSeeCraveAdmin) sections.push("craveAdmin");
    sections.push("utilities");
    return sections;
  };

  const toggleSection = (sectionName: "storeView" | "reports" | "craveAdmin" | "utilities") => {
    setExpandedSections((prev) => {
      const visible = getVisibleSections();

      if (prev[sectionName]) {
        // If the currently open section is clicked, switch to the next visible section
        const currentIdx = visible.indexOf(sectionName);
        const nextSection = visible[(currentIdx + 1) % visible.length];
        return {
          storeView: false,
          reports: false,
          craveAdmin: false,
          utilities: false,
          [nextSection]: true,
        };
      }

      // If a closed section is clicked, open it and close the others
      return {
        storeView: false,
        reports: false,
        craveAdmin: false,
        utilities: false,
        [sectionName]: true,
      };
    });
  };

  return (
    <Box
      className="bg-gradient-to-br from-[rgba(79,70,229,0.05)] to-[rgba(0,102,255,0.05)]"
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        pt: 0,
        overflow: "hidden",
        backdropFilter: "blur(10px)",
        borderRight: !isMobile ? "1px solid rgba(79, 70, 229, 0.1)" : "none",
        borderLeft: "none",
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
          py: 1,
          ...(!isMobile && {
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-track": {
              background: "rgba(79, 70, 229, 0.05)",
              borderRadius: "3px",
            },
            "&::-webkit-scrollbar-thumb": {
              background:
                "/* @noflip */ linear-gradient(135deg, rgba(79, 70, 229, 0.3) 0%, rgba(0, 102, 255, 0.3) 100%)",
              borderRadius: "3px",
              "&:hover": {
                background:
                  "/* @noflip */ linear-gradient(135deg, rgba(79, 70, 229, 0.5) 0%, rgba(0, 102, 255, 0.5) 100%)",
              },
            },
          }),
        }}
      >
        {canSeeStoreView && (
          <StoreViewSection
            isExpanded={expandedSections.storeView}
            onToggle={() => toggleSection("storeView")}
            userHasPermission={userHasPermission}
            isMobile={isMobile}
            onItemSelect={onItemSelect}
          />
        )}

        {canSeeReports && (
          <>
            <Divider sx={{ my: 1, mx: 2 }} />
            <ReportsSection
              isExpanded={expandedSections.reports}
              onToggle={() => toggleSection("reports")}
              userHasPermission={userHasPermission}
              isMobile={isMobile}
              onItemSelect={onItemSelect}
            />
          </>
        )}

        {canSeeCraveAdmin && (
          <>
            <Divider sx={{ my: 1, mx: 2 }} />
            <CraveAdminSection
              isExpanded={expandedSections.craveAdmin}
              onToggle={() => toggleSection("craveAdmin")}
              userHasPermission={userHasPermission}
              isMobile={isMobile}
              onItemSelect={onItemSelect}
            />
          </>
        )}

        <Divider sx={{ my: 1, mx: 2 }} />
        <UtilitiesSection
          isExpanded={expandedSections.utilities}
          onToggle={() => toggleSection("utilities")}
          userHasPermission={userHasPermission}
          onLogoutClick={handleLogout}
          isMobile={isMobile}
          onItemSelect={onItemSelect}
        />
      </Box>
    </Box>
  );
};
